import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronUp, MessageSquare, Plus, Send, Paperclip, X, User, Bot, Download, Check, Settings } from 'lucide-react';
import { toast } from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const ApplicantChatAccordion = ({ applicationId, jobId, candidateId }) => {
  const { user } = useAuth();
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showCreateChat, setShowCreateChat] = useState(false);
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  
  // Create chat form state
  const [newChatForm, setNewChatForm] = useState({
    chat_title: '',
    initial_context: '',
    include_jobseeker: true,
    include_jobposting: true,
    include_applicationnotes: true,
    include_jobmatches: true,
    include_application_timeline: false,
    include_company_research: false,
    include_career_progression: false,
    include_jobnotes: false
  });

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch chat sessions when accordion is expanded
  useEffect(() => {
    if (isExpanded && applicationId) {
      fetchChatSessions();
    }
  }, [isExpanded, applicationId]);

  // Fetch messages when a chat is selected
  useEffect(() => {
    if (selectedChatId) {
      fetchMessages();
    }
  }, [selectedChatId]);

  const fetchChatSessions = async () => {
    setLoading(true);
    try {
        console.log("Fetching chat sessions for application:", applicationId);
      const response = await fetch(`${API_BASE_URL}/applicant-chat/application/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch chat sessions');
      
      setChatSessions(data.data || []);
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
      toast.error('Failed to load chat sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    setMessagesLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/applicant-chat/${selectedChatId}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch messages');
      
      setMessages(data.data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleCreateChat = async () => {
    if (!newChatForm.chat_title.trim()) {
      toast.error('Please enter a chat title');
      return;
    }

    setCreatingChat(true);
    try {
      const response = await fetch(`${API_BASE_URL}/applicant-chat/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          application_id: applicationId,
          ...newChatForm
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create chat');
      
      // Refresh chat sessions and select the new chat
      await fetchChatSessions();
      setSelectedChatId(data.data.chat_id);
      setShowCreateChat(false);
      
      // Reset form
      setNewChatForm({
        chat_title: '',
        initial_context: '',
        include_jobseeker: true,
        include_jobposting: true,
        include_applicationnotes: true,
        include_jobmatches: true,
        include_application_timeline: false,
        include_company_research: false,
        include_career_progression: false,
        include_jobnotes: false
      });
      
      toast.success('Chat created successfully');
    } catch (error) {
      console.error('Error creating chat:', error);
      toast.error('Failed to create chat');
    } finally {
      setCreatingChat(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && selectedFiles.length === 0) return;

    setSendingMessage(true);
    try {
      // Send message
      const response = await fetch(`${API_BASE_URL}/applicant-chat/${selectedChatId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: newMessage.trim(),
          sender: 'recruiter'
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send message');
      
      const messageId = data.data.message_id;
      
      // Upload files if any
      if (selectedFiles.length > 0) {
        await uploadFiles(messageId);
      }
      
      // Clear input and refresh messages
      setNewMessage('');
      setSelectedFiles([]);
      await fetchMessages();
      
      // Generate AI response
      await generateAIResponse();
      
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const uploadFiles = async (messageId) => {
    setUploadingFiles(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${API_BASE_URL}/applicant-chat/${selectedChatId}/messages/${messageId}/attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to upload files');
      
      toast.success('Files uploaded successfully');
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploadingFiles(false);
    }
  };

  const generateAIResponse = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/applicant-chat/${selectedChatId}/ai-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate AI response');
      
      // Refresh messages to show AI response
      await fetchMessages();
    } catch (error) {
      console.error('Error generating AI response:', error);
      toast.error('Failed to generate AI response');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedFiles.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }
    
    // Check file sizes (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    const oversizedFiles = files.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      toast.error('Some files exceed 10MB limit');
      return;
    }
    
    setSelectedFiles(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleKeyPress = (e) => {
    // Allow Enter and Alt+Enter to create new lines
    // No keyboard shortcuts for sending messages
    return;
  };

  // Hardcoded list of authorized user IDs
  const AUTHORIZED_USER_IDS = [
    'bbec3e49-e19a-4a62-bbe5-d73244306f69', // Replace with actual user IDs
    // Add more user IDs as needed
  ];
  
  // Check if current user is authorized to see the chat
  const isUserAuthorized = user && AUTHORIZED_USER_IDS.includes(user.id);
  
  // Don't render anything if user is not authorized
  if (!isUserAuthorized) {
    return null;
  }

  return (
    <div className="card mb-4">
      <div className="card-header">
        <button
          className="btn btn-link text-decoration-none p-0 w-100 d-flex justify-content-between align-items-center"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="d-flex align-items-center">
            <MessageSquare size={20} className="text-primary me-2" />
            <span className="fw-medium">AI Chat about this Candidate</span>
          </div>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>
      
      {isExpanded && (
        <div className="card-body p-0" style={{ height: '70vh', display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Controls Header */}
              <div className="border-bottom p-3">
                <div className="d-flex align-items-center gap-3">
                  {/* Chat Dropdown */}
                  <div className="flex-grow-1">
                    {chatSessions.length === 0 ? (
                      <div className="text-muted">
                        <MessageSquare size={16} className="me-2" />
                        No chat sessions yet
                      </div>
                    ) : (
                      <div className="dropdown">
                        <button
                          className="btn btn-outline-secondary dropdown-toggle d-flex align-items-center w-100 justify-content-between"
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          style={{ textAlign: 'left' }}
                        >
                          <span className="text-truncate">
                            {selectedChatId ? 
                              chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.chat_title || 'Select a chat'
                              : 'Select a chat session'
                            }
                          </span>
                        </button>
                        <ul className="dropdown-menu w-100">
                          {chatSessions.map((session, index) => (
                            <li key={session.id || session.chat_id || `chat-session-${index}`}>
                              <a
                                className={`dropdown-item ${selectedChatId === (session.id || session.chat_id) ? 'active' : ''}`}
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setSelectedChatId(session.id || session.chat_id);
                                }}
                              >
                                <div className="fw-medium">{session.chat_title}</div>
                                <small className="text-muted">
                                  {formatTimestamp(session.created_at)} • {session.message_count || 0} messages
                                </small>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Settings Button */}
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setShowChatSettings(true)}
                    title="Chat Settings"
                    disabled={!selectedChatId}
                  >
                    <Settings size={16} />
                  </button>

                  {/* New Chat Button */}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => setShowCreateChat(true)}
                    title="New Chat"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-grow-1 d-flex flex-column">
                {selectedChatId ? (
                  <>
                    {/* Messages */}
                    <div 
                      className="flex-grow-1 p-3" 
                      style={{ 
                        overflowY: 'auto', 
                        minHeight: 0, 
                        maxHeight: 'calc(70vh - 180px)' 
                      }}
                    >
                      {messagesLoading ? (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading messages...</span>
                          </div>
                        </div>
                      ) : messages.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          <MessageSquare size={32} className="mb-2" />
                          <p className="mb-0">No messages yet. Start the conversation!</p>
                        </div>
                      ) : (
                        <div className="chat-messages">
                          {messages.map((message, index) => (
                            <div
                              key={message.id || message.message_id || `message-${index}`}
                              className={`mb-3 d-flex ${
                                message.sender === 'recruiter' ? 'justify-content-end' : 'justify-content-start'
                              }`}
                            >
                              <div
                                className={`p-3 rounded-3 ${
                                  message.sender === 'recruiter'
                                    ? 'bg-primary text-white'
                                    : 'bg-light text-dark'
                                }`}
                                style={{ 
                                  maxWidth: '70%',
                                  wordWrap: 'break-word',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'break-word'
                                }}
                              >
                                <div className="d-flex align-items-center mb-1">
                                  {message.sender === 'recruiter' ? (
                                    <User size={16} className="me-2" />
                                  ) : (
                                    <Bot size={16} className="me-2" />
                                  )}
                                  <small className="opacity-75">
                                    {message.sender === 'recruiter' ? 'You' : 'AI Assistant'}
                                  </small>
                                </div>
                                <div 
                                  className="message-content"
                                  style={{ 
                                    whiteSpace: 'pre-wrap',
                                    wordWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word'
                                  }}
                                >
                                  {message.sender === 'ai' ? (
                                    <ReactMarkdown 
                                      components={{
                                        p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                                        strong: ({children}) => <strong>{children}</strong>,
                                        em: ({children}) => <em>{children}</em>,
                                        ul: ({children}) => <ul className="ps-3 mb-2">{children}</ul>,
                                        ol: ({children}) => <ol className="ps-3 mb-2">{children}</ol>,
                                        li: ({children}) => <li className="mb-1">{children}</li>,
                                        blockquote: ({children}) => <blockquote className="border-start border-3 ps-3 mb-2 opacity-75">{children}</blockquote>,
                                        code: ({children}) => <code className="bg-dark bg-opacity-10 px-1 rounded text-sm">{children}</code>,
                                        pre: ({children}) => <pre className="bg-dark bg-opacity-10 p-2 rounded mb-2 overflow-auto text-sm">{children}</pre>,
                                        h1: ({children}) => <h5 className="mb-2 fw-bold">{children}</h5>,
                                        h2: ({children}) => <h6 className="mb-2 fw-bold">{children}</h6>,
                                        h3: ({children}) => <h6 className="mb-2 fw-semibold">{children}</h6>,
                                        h4: ({children}) => <h6 className="mb-2">{children}</h6>,
                                        h5: ({children}) => <h6 className="mb-2">{children}</h6>,
                                        h6: ({children}) => <h6 className="mb-2">{children}</h6>
                                      }}
                                    >
                                      {message.message}
                                    </ReactMarkdown>
                                  ) : (
                                    message.message
                                  )}
                                </div>
                                {message.attachments && message.attachments.length > 0 && (
                                  <div className="mt-2">
                                    {message.attachments.map((attachment, index) => (
                                      <div key={attachment.id || `${message.id || message.message_id}-attachment-${index}`} className="d-flex align-items-center mt-1">
                                        <Paperclip size={14} className="me-1" />
                                        <a
                                          href={attachment.file_url || attachment.storage_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-decoration-none small"
                                          style={{ 
                                            wordBreak: 'break-all',
                                            overflowWrap: 'break-word'
                                          }}
                                        >
                                          {attachment.file_name || attachment.filename}
                                        </a>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                <div className="small opacity-50 mt-1">
                                  {formatTimestamp(message.created_at)}
                                </div>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="border-top p-3" style={{ flexShrink: 0 }}>
                      {/* Selected Files */}
                      {selectedFiles.length > 0 && (
                        <div className="mb-2">
                          <div className="d-flex flex-wrap gap-2">
                            {selectedFiles.map((file, index) => (
                              <div key={`${file.name}-${file.size}-${index}`} className="d-flex align-items-center bg-light px-2 py-1 rounded">
                                <Paperclip size={14} className="me-1" />
                                <span className="small me-2">
                                  {file.name} ({formatFileSize(file.size)})
                                </span>
                                <button
                                  className="btn btn-link btn-sm p-0 text-danger"
                                  onClick={() => removeFile(index)}
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Message Input with Floating Buttons */}
                      <div className="position-relative">
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="d-none"
                          multiple
                          accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif"
                          onChange={handleFileSelect}
                        />
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Type your message here..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          disabled={sendingMessage}
                          style={{ paddingRight: '80px', resize: 'none' }}
                        />
                        <div className="position-absolute" style={{ bottom: '8px', right: '8px' }}>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-light btn-sm rounded-circle d-flex align-items-center justify-content-center"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={selectedFiles.length >= 5}
                              style={{ width: '32px', height: '32px' }}
                              title="Attach files"
                            >
                              <Paperclip size={14} />
                            </button>
                            <button
                              className="btn btn-primary btn-sm rounded-circle d-flex align-items-center justify-content-center"
                              onClick={handleSendMessage}
                              disabled={sendingMessage || (!newMessage.trim() && selectedFiles.length === 0)}
                              style={{ width: '32px', height: '32px' }}
                              title="Send message"
                            >
                              {sendingMessage ? (
                                <div className="spinner-border spinner-border-sm" role="status" style={{ width: '14px', height: '14px' }}>
                                  <span className="visually-hidden">Sending...</span>
                                </div>
                              ) : (
                                <Send size={14} />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="text-center text-muted">
                      <MessageSquare size={48} className="mb-3" />
                      <p className="mb-0">
                        {chatSessions.length === 0 
                          ? 'Create a new chat to get started' 
                          : 'Select a chat session to start messaging'
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Create Chat Modal */}
      {showCreateChat && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create New Chat</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCreateChat(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Chat Title</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter chat title..."
                    value={newChatForm.chat_title}
                    onChange={(e) => setNewChatForm(prev => ({ ...prev, chat_title: e.target.value }))}
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label">Initial Context (Optional)</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Add any initial context or questions..."
                    value={newChatForm.initial_context}
                    onChange={(e) => setNewChatForm(prev => ({ ...prev, initial_context: e.target.value }))}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Include Context Data</label>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_jobseeker}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_jobseeker: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Candidate Profile
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_jobposting}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_jobposting: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Job Posting
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_applicationnotes}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_applicationnotes: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Application Notes
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_jobmatches}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_jobmatches: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Job Matches
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_application_timeline}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_application_timeline: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Application Timeline
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_company_research}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_company_research: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Company Research
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_career_progression}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_career_progression: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Career Progression
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newChatForm.include_jobnotes}
                          onChange={(e) => setNewChatForm(prev => ({ ...prev, include_jobnotes: e.target.checked }))}
                        />
                        <label className="form-check-label">
                          Job Notes
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateChat(false)}
                  disabled={creatingChat}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateChat}
                  disabled={creatingChat || !newChatForm.chat_title.trim()}
                >
                  {creatingChat ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Creating...</span>
                      </div>
                      Creating...
                    </>
                  ) : (
                    'Create Chat'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Chat Settings Modal */}
      {showChatSettings && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Chat Settings</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowChatSettings(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <strong>Note:</strong> This is a view-only display of the current chat's settings. 
                  To modify settings, you'll need to create a new chat session.
                </div>
                
                {selectedChatId && (
                  <>
                    <div className="mb-3">
                      <label className="form-label">Chat Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.chat_title || ''}
                        readOnly
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Initial Context</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.initial_context || ''}
                        readOnly
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Context Data Included</label>
                      <div className="row g-2">
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_jobseeker || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Candidate Profile
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_jobposting || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Job Posting
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_applicationnotes || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Application Notes
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_jobmatches || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Job Matches
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_application_timeline || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Application Timeline
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_company_research || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Company Research
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_career_progression || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Career Progression
                            </label>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.include_jobnotes || false}
                              disabled
                            />
                            <label className="form-check-label">
                              Job Notes
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Chat Statistics</label>
                      <div className="row">
                        <div className="col-md-6">
                          <div className="small text-muted">
                            <strong>Created:</strong> {formatTimestamp(chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.created_at)}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="small text-muted">
                            <strong>Messages:</strong> {chatSessions.find(s => (s.id || s.chat_id) === selectedChatId)?.message_count || 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowChatSettings(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicantChatAccordion; 